
import { head, isEmpty, filter, choose, fold, unzip, forAll, cons, exists, concat, append, empty, singleton, collect, map, tryFind } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { concat as concat_1, toFail } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { List_distinct } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";
import { equals, createAtom, comparePrimitives, compare, stringHash } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { addStateToLoadedComponents, getStateAndDependencies, loadedComponentIsEqualExInputDefault, simpleDesignOfLoadedComponents, loadedComponentIsEqual, stateIsEqual, sheetFromCanvas } from "./CanvasExtractor.js";
import { FastSimulation__get_SimulatedCanvasState, FastSimulation_$reflection, SimulationData_$reflection, WaveTables, SimulationData, FastSimulation, SimulatedDesign, SheetPort, SimulationRunStatus, FastSimulation__get_SimulatedTopSheet } from "./SimTypes.js";
import { LoadedComponent, LoadedComponent_$reflection, NumberBase, SimpleDesign, Component, ComponentType, CustomComponentType, Component__getPort_307C1D41 } from "../Common/CommonTypes.js";
import { empty as empty_1, toList, change, tryFind as tryFind_1, ofList, FSharpMap__get_Item } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { expandArraySheets } from "./ArrayElaborate.js";
import { singleton as singleton_1, add, contains } from "../fable_modules/fable-library-js.5.13.0/Set.js";
import { defaultArg } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { Result_DefaultValue, Result_MapError, Result_Bind, Result_Map, FSharpResult$2 } from "../fable_modules/fable-library-js.5.13.0/Result.js";
import { SimulationError_$reflection, AlgebraNotImplemented, SimulationError, SimulationErrorType } from "./SimGraphTypes.js";
import { getSimulationIOs, runCanvasStateChecksAndBuildGraph } from "./GraphBuilder.js";
import { mergeDependencies } from "./GraphMerger.js";
import { analyseSimulationGraph } from "./SimulationGraphAnalyser.js";
import { SimLogKind, record, beginInvocation } from "./SimLog.js";
import { getTimeMs } from "../Common/TimeHelpers.js";
import { dbg, error } from "../Shared/Log.js";
import { buildFastSimulationFData, buildFastSimulationWith } from "./FastSim/FastBuild.js";
import { hasSynchronousComponents } from "./SynchronousUtils.js";
import { Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { record_type, union_type, list_type, int32_type, string_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { emptyFastSimulation, simulationPlaceholder } from "./FastSim/FastCreate.js";

export function cap(sheet) {
    return sheet.toLocaleUpperCase();
}

/**
 * look up a sheet in a set of loaded components
 */
export function getSheet(ldcs, openSheet) {
    const matchValue = tryFind((ldc) => (cap(ldc.Name) === cap(openSheet)), ldcs);
    if (matchValue != null) {
        return matchValue;
    }
    else {
        return toFail(`getSheet failed to look up '${openSheet}' in ${map((ldc_1) => ldc_1.Name, ldcs)}`);
    }
}

/**
 * look up a sheet in a set of loaded components, return [] or a list of the matching LoadedComponent
 */
export function getLdcList(ldcs, openSheet) {
    const matchValue = tryFind((ldc) => (cap(ldc.Name) === cap(openSheet)), ldcs);
    if (matchValue != null) {
        return matchValue;
    }
    else {
        return toFail(`getSheet failed to look up '${openSheet}' in ${map((ldc_1) => ldc_1.Name, ldcs)}`);
    }
}

export function getDirectDependencies(cs_, cs__1) {
    return collect((comp) => {
        const matchValue = comp.Type;
        if (matchValue.tag === 26) {
            return singleton([comp.Label, matchValue.fields[0].Name]);
        }
        else {
            return empty();
        }
    }, cs_);
}

export function childrenOf(ldcs, sheet) {
    const tupledArg = getSheet(ldcs, sheet).CanvasState;
    return getDirectDependencies(tupledArg[0], tupledArg[1]);
}

/**
 * Sheets needed to simulate sheet with name sheet.
 * Sheets form a dependency tree.
 * ldcs is a list of loaded components which must include sheet
 */
export function sheetsNeeded(ldcs, sheet) {
    const children = map((tuple) => tuple[1], childrenOf(ldcs, sheet));
    return List_distinct(append(singleton(sheet), append(children, concat(map((sheet_1) => sheetsNeeded(ldcs, sheet_1), children)))), {
        Equals: (x, y) => (x === y),
        GetHashCode: (x) => (stringHash(x) | 0),
    });
}

/**
 * canvasState: extracted canvasState from draw block.
 * projLdcs: ldcs from project (current sheet ldc may be outofdate)
 * diagramName: name of current open sheet.
 * return updated list of all LDCs
 */
export function getUpdatedLoadedComponentState(diagramName, canvasState_, canvasState__1, projLdcs) {
    const canvasState = [canvasState_, canvasState__1];
    const ldcIsOpen = (ldc) => (ldc.Name === diagramName);
    const ldc$0027 = sheetFromCanvas(tryFind(ldcIsOpen, projLdcs), diagramName, canvasState[0], canvasState[1]);
    const ldcs = map((ldc_1) => {
        if (ldcIsOpen(ldc_1)) {
            return ldc$0027;
        }
        else {
            return ldc_1;
        }
    }, projLdcs);
    if (!exists(ldcIsOpen, ldcs)) {
        return cons(ldc$0027, ldcs);
    }
    else {
        return ldcs;
    }
}

/**
 * gets the status of the simulation given current canvasState and project
 */
export function getCurrentSimulationState(canvState_, canvState__1, project, fs) {
    const canvState = [canvState_, canvState__1];
    if (project != null) {
        if (FastSimulation__get_SimulatedTopSheet(fs) === "") {
            return SimulationRunStatus.SimEmpty;
        }
        else {
            const p = project;
            const simIsUpToDate = forAll((ldc) => {
                const matchValue = p.OpenFileName === ldc.Name;
                const matchValue_1 = tryFind((ldc$0027) => (ldc$0027.Name === ldc.Name), p.LoadedComponents);
                if (matchValue_1 != null) {
                    if (matchValue) {
                        const tupledArg = ldc.CanvasState;
                        return stateIsEqual(tupledArg[0], tupledArg[1], canvState[0], canvState[1]);
                    }
                    else {
                        const ldc$0027_1 = matchValue_1;
                        return loadedComponentIsEqual(ldc, ldc$0027_1);
                    }
                }
                else {
                    return false;
                }
            }, fs.Design.DesignAsDrawn);
            const matchValue_3 = p.OpenFileName === FastSimulation__get_SimulatedTopSheet(fs);
            if (simIsUpToDate) {
                if (matchValue_3) {
                    return SimulationRunStatus.SimValidSameSheet;
                }
                else {
                    return SimulationRunStatus.SimValidDifferentSheet;
                }
            }
            else {
                return SimulationRunStatus.SimOutOfDate;
            }
        }
    }
    else {
        return SimulationRunStatus.SimNoProject;
    }
}

/**
 * Helper used convert port into SheetPort for use by wave simulator determining connectivity
 * within a design sheet.
 * name is the name of the containing sheet.
 */
export function portSheetPort(compsWithIds, name, port) {
    const compPort = Component__getPort_307C1D41(FSharpMap__get_Item(compsWithIds, port.HostId), port.Id);
    if (compPort != null) {
        return new SheetPort(name, compPort);
    }
    else {
        return undefined;
    }
}

/**
 * canvasState: extracted canvasState from draw block.
 * loadedComponents: from project
 * diagramName: name of current open sheet.
 * save all needed by simulation ldcs in the FastSimulation record.
 * The top sheet name must be saved separately - since if a simulation is being refreshed it must not change
 */
export function saveStateInSimulation(canvasState_, canvasState__1, openFileName, loadedComponents, _arg1_, _arg1__1, copyNames, fs) {
    const canvasState = [canvasState_, canvasState__1];
    const _arg = [_arg1_, _arg1__1];
    const drawnCanvasState = _arg[0];
    const diagramName = openFileName;
    const ldcs = getUpdatedLoadedComponentState(diagramName, canvasState[0], canvasState[1], loadedComponents);
    let asDrawn;
    const drawn = getUpdatedLoadedComponentState(diagramName, drawnCanvasState[0], drawnCanvasState[1], _arg[1]);
    asDrawn = map((openSheet) => getSheet(drawn, openSheet), sheetsNeeded(drawn, diagramName));
    const updatedLdcs = map((openSheet_1) => getSheet(ldcs, openSheet_1), sheetsNeeded(ldcs, diagramName));
    const patternInput_1 = unzip(map((ldc) => {
        const patternInput = ldc.CanvasState;
        const compsWithIds = ofList(map((comp_1) => {
            let comp, matchValue, matchValue_1, ins, outs;
            return [comp_1.Id, (comp = comp_1, (matchValue = comp.Type, (matchValue_1 = tryFind_1(comp.Id, copyNames), (matchValue.tag === 26) ? ((matchValue_1 != null) ? ((ins = matchValue_1[0], (outs = matchValue_1[1], new Component(comp.Id, new ComponentType(/* Custom */ 26, [new CustomComponentType(matchValue.fields[0].Name, ins, outs, matchValue.fields[0].Form, matchValue.fields[0].ParameterBindings, matchValue.fields[0].Description)]), comp.Label, comp.InputPorts, comp.OutputPorts, comp.X, comp.Y, comp.H, comp.W, comp.SymbolInfo, comp.SlotInfo)))) : comp) : comp)))];
        }, patternInput[0]), {
            Compare: (x, y) => (compare(x, y) | 0),
        });
        const portSheetPort_1 = (port) => portSheetPort(compsWithIds, ldc.Name, port);
        const addConnToPort = (portOpt, conn, pMap) => {
            if (portOpt != null) {
                return change(portOpt, (_arg_1) => {
                    if (_arg_1 == null) {
                        return singleton(conn);
                    }
                    else {
                        return cons(conn, _arg_1);
                    }
                }, pMap);
            }
            else {
                return pMap;
            }
        };
        return [[ldc.Name, compsWithIds], toList(fold((pMap_1, conn_1) => addConnToPort(portSheetPort_1(conn_1.Target), conn_1, addConnToPort(portSheetPort_1(conn_1.Source), conn_1, pMap_1)), empty_1({
            Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
        }), patternInput[1]))];
    }, updatedLdcs));
    const Design = new SimulatedDesign(updatedLdcs, asDrawn, fs.Design.DesignTopSheet, ofList(patternInput_1[0], {
        Compare: (x_2, y_2) => (comparePrimitives(x_2, y_2) | 0),
    }), ofList(concat(patternInput_1[1]), {
        Compare: (x_3, y_3) => (compare(x_3, y_3) | 0),
    }));
    return new FastSimulation(fs.ClockTick, fs.MaxArraySize, fs.FGlobalInputComps, fs.FConstantComps, fs.FClockedComps, fs.FOrderedComps, fs.FIOActive, fs.FIOLinks, fs.FCompsByIndex, fs.FIndexOf, fs.FCustomOutputCompLookup, fs.NumStepArrays, fs.Drivers, fs.WaveIndex, Design, fs.StepCost);
}

/**
 * The design to send to the .NET sidecar: THE sheets this simulation was built from.
 * 
 * **Built from the simulation and not from the project, and that is the point.** The renderer has
 * already turned the project into the circuit it simulates - array components expanded into a
 * wrapper and a body - and this hands the sidecar that, so the two processes simulate the same
 * sheets rather than each deriving them from the same input and hoping to agree.
 * 
 * They had to agree exactly, because SimSetInputs names a component by ID across the wire and
 * expansion MINTS ids for everything it makes. Two expansions agreeing was luck: the same code
 * over the same list in the same order. Nothing enforced it and nothing tested it. Now only one
 * of the two ever expands, so there is nothing to agree about.
 * 
 * It is also why the wire format knows nothing about array components, and must not learn: a
 * SimpleSheet that crossed this boundary is an ordinary sheet by construction.
 */
export function designForSidecar(fs) {
    const d = simpleDesignOfLoadedComponents(fs.Design.DesignSheets);
    return new SimpleDesign(FastSimulation__get_SimulatedTopSheet(fs), d.Sheets);
}

function expandArrayDesign(diagramName, canvasState_, canvasState__1, loadedDependencies) {
    let matchValue_4, option_1, option_3;
    const canvasState = [canvasState_, canvasState__1];
    const matchValue = exists((ldc) => (ldc.ArrayInfo != null), loadedDependencies);
    if (matchValue) {
        const topSheet = tryFind((ldc_1) => (ldc_1.Name === diagramName), loadedDependencies);
        const patternInput = expandArraySheets(loadedDependencies);
        const expanded = patternInput[0];
        const reach = (seen, comps) => fold((seen_1, name) => {
            if (contains(name, seen_1)) {
                return seen_1;
            }
            else {
                const _arg = tryFind((ldc_2) => (ldc_2.Name === name), loadedDependencies);
                if (_arg != null) {
                    const ldc_3 = _arg;
                    return reach(add(name, seen_1), ldc_3.CanvasState[0]);
                }
                else {
                    return add(name, seen_1);
                }
            }
        }, seen, choose((comp) => {
            const matchValue_1 = comp.Type;
            if (matchValue_1.tag === 26) {
                return matchValue_1.fields[0].Name;
            }
            else {
                return undefined;
            }
        }, comps));
        const used = reach(singleton_1(diagramName, {
            Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
        }), canvasState[0]);
        const matchValue_3 = filter((tupledArg) => contains(tupledArg[0], used), patternInput[1]);
        if (isEmpty(matchValue_3)) {
            return new FSharpResult$2(/* Ok */ 0, [[(matchValue_4 = ((option_1 = topSheet, (option_1 != null) ? option_1.ArrayInfo : undefined)), (matchValue_4 != null) ? defaultArg((option_3 = tryFind((ldc_5) => (ldc_5.Name === diagramName), expanded), (option_3 != null) ? option_3.CanvasState : undefined), canvasState) : canvasState), expanded, patternInput[2]]]);
        }
        else {
            const problem = head(matchValue_3)[1];
            return new FSharpResult$2(/* Error */ 1, [new SimulationError(new SimulationErrorType(/* GenericSimError */ 20, [problem.Message]), undefined, problem.Components, empty())]);
        }
    }
    else {
        return new FSharpResult$2(/* Ok */ 0, [[canvasState, loadedDependencies, empty_1({
            Compare: (x, y) => (compare(x, y) | 0),
        })]]);
    }
}

/**
 * Extract circuit data from inputs and return a valid SimulationGraph or an error
 */
export function validateCircuitSimulation(diagramName, canvasState_, canvasState__1, loadedDependencies) {
    const canvasState = [canvasState_, canvasState__1];
    const matchValue = expandArrayDesign(diagramName, canvasState[0], canvasState[1], loadedDependencies);
    if (matchValue.tag === 0) {
        const loadedDependencies_1 = matchValue.fields[0][1];
        const canvasState_1 = matchValue.fields[0][0];
        const matchValue_1 = runCanvasStateChecksAndBuildGraph(canvasState_1[0], canvasState_1[1], loadedDependencies_1);
        if (matchValue_1.tag === 0) {
            const matchValue_2 = mergeDependencies(diagramName, matchValue_1.fields[0], canvasState_1[0], canvasState_1[1], loadedDependencies_1);
            if (matchValue_2.tag === 0) {
                const graph_1 = matchValue_2.fields[0];
                const matchValue_3 = analyseSimulationGraph(diagramName, graph_1, canvasState_1[1]);
                if (matchValue_3 == null) {
                    return new FSharpResult$2(/* Ok */ 0, [graph_1]);
                }
                else {
                    return new FSharpResult$2(/* Error */ 1, [matchValue_3]);
                }
            }
            else {
                return new FSharpResult$2(/* Error */ 1, [matchValue_2.fields[0]]);
            }
        }
        else {
            return new FSharpResult$2(/* Error */ 1, [matchValue_1.fields[0]]);
        }
    }
    else {
        return new FSharpResult$2(/* Error */ 1, [matchValue.fields[0]]);
    }
}

/**
 * Extract circuit data from inputs and return a checked SimulationData object or an error
 * SimulationData has some technical debt, it wraps FastSimulation adding some redundant data
 * 
 * `waveTables` says whether to build what only a wave viewer reads - see WaveTables.
 */
export function startCircuitSimulationWith(waveTables, simulationArraySize, diagramName, canvasState_, canvasState__1, loadedDependencies) {
    const canvasState = [canvasState_, canvasState__1];
    beginInvocation();
    const buildStart = getTimeMs();
    const asDrawn = [canvasState, loadedDependencies];
    const matchValue = expandArrayDesign(diagramName, canvasState[0], canvasState[1], loadedDependencies);
    if (matchValue.tag === 0) {
        const loadedDependencies_1 = matchValue.fields[0][1];
        const canvasState_1 = matchValue.fields[0][0];
        const matchValue_1 = validateCircuitSimulation(diagramName, canvasState_1[0], canvasState_1[1], loadedDependencies_1);
        if (matchValue_1.tag === 0) {
            const graph = matchValue_1.fields[0];
            return Result_Map((sd) => sd, (() => {
                try {
                    const matchValue_2 = buildFastSimulationWith(waveTables, simulationArraySize, diagramName, graph);
                    if (matchValue_2.tag === 1) {
                        return new FSharpResult$2(/* Error */ 1, [matchValue_2.fields[0]]);
                    }
                    else {
                        const fs_1 = saveStateInSimulation(canvasState_1[0], canvasState_1[1], diagramName, loadedDependencies_1, asDrawn[0], asDrawn[1], matchValue.fields[0][2], matchValue_2.fields[0]);
                        const patternInput = getSimulationIOs(canvasState_1[0]);
                        record(SimLogKind.SimBuild, diagramName, fs_1.FCompsByIndex.length, 0, 0, getTimeMs() - buildStart);
                        return new FSharpResult$2(/* Ok */ 0, [new SimulationData(fs_1, graph, patternInput[0], patternInput[1], hasSynchronousComponents(graph), NumberBase.Hex, 0)]);
                    }
                }
                catch (e_3) {
                    error(concat_1("exception during simulation: ", e_3.message, "\n", e_3.stack));
                    return new FSharpResult$2(/* Error */ 1, [new SimulationError(new SimulationErrorType(/* InternalError */ 19, [e_3]), undefined, empty(), empty())]);
                }
            })());
        }
        else {
            return new FSharpResult$2(/* Error */ 1, [matchValue_1.fields[0]]);
        }
    }
    else {
        return new FSharpResult$2(/* Error */ 1, [matchValue.fields[0]]);
    }
}

/**
 * A simulation with everything a wave viewer in this process needs, which is what almost every
 * caller wants. The sidecar is the exception and asks for NoWaveTables.
 */
export function startCircuitSimulation(simulationArraySize, diagramName, canvasState_, canvasState__1, loadedDependencies) {
    const canvasState = [canvasState_, canvasState__1];
    return startCircuitSimulationWith(WaveTables.WithWaveTables, simulationArraySize, diagramName, canvasState[0], canvasState[1], loadedDependencies);
}

export function startCircuitSimulationFData(simulationArraySize, diagramName, canvasState_, canvasState__1, loadedDependencies) {
    const canvasState = [canvasState_, canvasState__1];
    const asDrawn = [canvasState, loadedDependencies];
    const matchValue = expandArrayDesign(diagramName, canvasState[0], canvasState[1], loadedDependencies);
    if (matchValue.tag === 0) {
        const loadedDependencies_1 = matchValue.fields[0][1];
        const canvasState_1 = matchValue.fields[0][0];
        const matchValue_1 = runCanvasStateChecksAndBuildGraph(canvasState_1[0], canvasState_1[1], loadedDependencies_1);
        if (matchValue_1.tag === 0) {
            const matchValue_2 = mergeDependencies(diagramName, matchValue_1.fields[0], canvasState_1[0], canvasState_1[1], loadedDependencies_1);
            if (matchValue_2.tag === 0) {
                const graph_1 = matchValue_2.fields[0];
                const patternInput = getSimulationIOs(canvasState_1[0]);
                const matchValue_3 = analyseSimulationGraph(diagramName, graph_1, canvasState_1[1]);
                if (matchValue_3 == null) {
                    return Result_Map((sd) => sd, (() => {
                        try {
                            const matchValue_4 = buildFastSimulationFData(simulationArraySize, diagramName, graph_1);
                            return (matchValue_4.tag === 1) ? (new FSharpResult$2(/* Error */ 1, [matchValue_4.fields[0]])) : (new FSharpResult$2(/* Ok */ 0, [new SimulationData(saveStateInSimulation(canvasState_1[0], canvasState_1[1], diagramName, loadedDependencies_1, asDrawn[0], asDrawn[1], matchValue.fields[0][2], matchValue_4.fields[0]), graph_1, patternInput[0], patternInput[1], hasSynchronousComponents(graph_1), NumberBase.Hex, 0)]));
                        }
                        catch (matchValue_5) {
                            if (matchValue_5 instanceof AlgebraNotImplemented) {
                                return new FSharpResult$2(/* Error */ 1, [matchValue_5.Data0]);
                            }
                            else {
                                error(concat_1("exception during simulation: ", matchValue_5.message, "\n", matchValue_5.stack));
                                return new FSharpResult$2(/* Error */ 1, [new SimulationError(new SimulationErrorType(/* InternalError */ 19, [matchValue_5]), undefined, empty(), empty())]);
                            }
                        }
                    })());
                }
                else {
                    return new FSharpResult$2(/* Error */ 1, [matchValue_3]);
                }
            }
            else {
                return new FSharpResult$2(/* Error */ 1, [matchValue_2.fields[0]]);
            }
        }
        else {
            return new FSharpResult$2(/* Error */ 1, [matchValue_1.fields[0]]);
        }
    }
    else {
        return new FSharpResult$2(/* Error */ 1, [matchValue.fields[0]]);
    }
}

export class SimCache extends Record {
    constructor(Name, ClockTickRefresh, StoredState, StoredResult, FastSim) {
        super();
        this.Name = Name;
        this.ClockTickRefresh = (ClockTickRefresh | 0);
        this.StoredState = StoredState;
        this.StoredResult = StoredResult;
        this.FastSim = FastSim;
    }
}

export function SimCache_$reflection() {
    return record_type("Simulator.SimCache", [], SimCache, () => [["Name", string_type], ["ClockTickRefresh", int32_type], ["StoredState", list_type(LoadedComponent_$reflection())], ["StoredResult", union_type("Microsoft.FSharp.Core.FSharpResult`2", [SimulationData_$reflection(), SimulationError_$reflection()], FSharpResult$2, () => [[["ResultValue", SimulationData_$reflection()]], [["ErrorValue", SimulationError_$reflection()]]])], ["FastSim", FastSimulation_$reflection()]]);
}

export function simCacheInit() {
    return new SimCache("", 0, empty(), new FSharpResult$2(/* Ok */ 0, [new SimulationData(simulationPlaceholder, empty_1({
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    }), empty(), empty(), false, NumberBase.Hex, 0)]), simulationPlaceholder);
}

export let simCache = createAtom(simCacheInit());

/**
 * The currently active FastSimulation - a dummy if none has been built. Callers on wave-sim
 * paths must be guarded by the MODEL saying a wave simulation is live (WaveSimSheet, ws.State):
 * under mutual exclusion the slot then holds that simulation and nothing else can.
 */
export function getFastSim() {
    return simCache().FastSim;
}

export function cacheIsEqual(cache, ldcs) {
    const matchValue = cache.StoredResult;
    if (matchValue.tag === 0) {
        return forAll((ldc$0027) => {
            let option_1;
            return equals(true, (option_1 = tryFind((ldc$0027$0027) => (ldc$0027$0027.Name === ldc$0027.Name), ldcs), (option_1 != null) ? loadedComponentIsEqual(ldc$0027, option_1) : undefined));
        }, FastSimulation__get_SimulatedCanvasState(matchValue.fields[0].FastSim));
    }
    else {
        return false;
    }
}

export function storedstateisEqual(cache, ldcs) {
    const matchValue = cache.StoredState;
    if (isEmpty(matchValue)) {
        return false;
    }
    else {
        return forAll((ldc$0027) => {
            let option_1;
            return equals(true, (option_1 = tryFind((ldc$0027$0027) => (ldc$0027$0027.Name === ldc$0027.Name), ldcs), (option_1 != null) ? loadedComponentIsEqualExInputDefault(ldc$0027, option_1) : undefined));
        }, matchValue);
    }
}

export function makeDummySimulationError(msg) {
    return new SimulationError(new SimulationErrorType(/* GenericSimError */ 20, [msg]), undefined, empty(), empty());
}

/**
 * Does one sheet of a design build into a simulation? The question behind the Simulation tab's
 * verdict button and the wave viewer's price, and the cheapest way to ask it: the graph is checked
 * and thrown away, with no step arrays allocated.
 * 
 * **THE one place that says how a design is checked**, and it is written down because it was
 * written twice and the two drifted. The whole design goes to validateCircuitSimulation - the
 * named sheet included - while getStateAndDependencies is asked only for that sheet's canvas: it
 * removes the sheet from the list it returns, and what a sheet IS rather than what is drawn on it
 * (its parameters, and whether it is an ARRAY COMPONENT whose hardware is several copies of what
 * is drawn on it) is on the sheet and not on the canvas. Passing the shortened list on left
 * nothing able to say the sheet being checked was an array component, so the checks ran on its
 * unexpanded canvas and refused its own IO as being on a sheet that is not an array component.
 * 
 * `ldcs` is the design as the model holds it now - ModelHelpers.designOf, which puts the canvas
 * being drawn in place of the open sheet's saved copy.
 */
export function validateSheetOfDesign(sheetName, ldcs) {
    return Result_Bind((tupledArg) => {
        const state = tupledArg[1];
        return validateCircuitSimulation(sheetName, state[0], state[1], ldcs);
    }, Result_MapError(makeDummySimulationError, getStateAndDependencies(sheetName, ldcs)));
}

/**
 * A simulation carrier with no simulation in it: what the renderer holds when the .NET sidecar
 * is the one simulating.
 * 
 * It carries the DESIGN - which answers every structure question the UI asks - the top sheet's
 * IOs, whether the circuit is clocked, and the checked graph, exactly as a real build would. What
 * it does not hold is the expansion: no components, no step arrays, no drivers. Widths and data
 * come from the sidecar - widths through the port slices, data through the reads - and the local
 * fields every gate consults say so: NumStepArrays 0, empty maps.
 * 
 * The design is still validated here, by the same checks a build runs, so a design that cannot
 * simulate is refused synchronously with the same error the user has always seen - components
 * highlighted and all. What is skipped is everything after validation: the flatten, the arrays,
 * the ordering.
 */
export function designOnlySimulation(simulationArraySize, diagramName, canvasState_, canvasState__1, loadedDependencies) {
    let bind$0040;
    const canvasState = [canvasState_, canvasState__1];
    const asDrawn = [canvasState, loadedDependencies];
    const matchValue = expandArrayDesign(diagramName, canvasState[0], canvasState[1], loadedDependencies);
    if (matchValue.tag === 0) {
        const loadedDependencies_1 = matchValue.fields[0][1];
        const canvasState_1 = matchValue.fields[0][0];
        const matchValue_1 = validateCircuitSimulation(diagramName, canvasState_1[0], canvasState_1[1], loadedDependencies_1);
        if (matchValue_1.tag === 0) {
            const graph = matchValue_1.fields[0];
            const fs_1 = saveStateInSimulation(canvasState_1[0], canvasState_1[1], diagramName, loadedDependencies_1, asDrawn[0], asDrawn[1], matchValue.fields[0][2], (bind$0040 = emptyFastSimulation(diagramName), new FastSimulation(bind$0040.ClockTick, simulationArraySize, bind$0040.FGlobalInputComps, bind$0040.FConstantComps, bind$0040.FClockedComps, bind$0040.FOrderedComps, bind$0040.FIOActive, bind$0040.FIOLinks, bind$0040.FCompsByIndex, bind$0040.FIndexOf, bind$0040.FCustomOutputCompLookup, bind$0040.NumStepArrays, bind$0040.Drivers, bind$0040.WaveIndex, bind$0040.Design, bind$0040.StepCost)));
            const patternInput = getSimulationIOs(canvasState_1[0]);
            return new FSharpResult$2(/* Ok */ 0, [new SimulationData(fs_1, graph, patternInput[0], patternInput[1], hasSynchronousComponents(graph), NumberBase.Hex, 0)]);
        }
        else {
            return new FSharpResult$2(/* Error */ 1, [matchValue_1.fields[0]]);
        }
    }
    else {
        return new FSharpResult$2(/* Error */ 1, [matchValue.fields[0]]);
    }
}

/**
 * Start up a simulation, doing all necessary checks and generating simulation errors
 * if necesary. The code to do this is quite long so results are memoized.
 * 
 * `localBuild` false builds the design-only carrier above instead of a real simulation - what
 * the renderer wants when the sidecar simulates. Truth tables always pass true: they read their
 * own local simulation whatever mode the simulators are in.
 */
export function prepareSimulationMemoized(localBuild, isWaveSim, simulationArraySize, openFileName, diagramName, canvasState_, canvasState__1, loadedDependencies) {
    let StoredResult;
    const canvasState = [canvasState_, canvasState__1];
    const storedArraySize = simCache().FastSim.MaxArraySize | 0;
    const storedIsLocal = simCache().FastSim.NumStepArrays > 0;
    const ldcs = addStateToLoadedComponents(openFileName, canvasState[0], canvasState[1], loadedDependencies);
    if ((((storedArraySize === simulationArraySize) && (localBuild === storedIsLocal)) && (diagramName === simCache().Name)) && cacheIsEqual(simCache(), ldcs)) {
        return [simCache().StoredResult, canvasState];
    }
    else {
        dbg(32, `new ${(isWaveSim ? "waveform simulation" : "simulation") + (localBuild ? "" : " (design-only: the .NET sidecar simulates)")} of ${simulationArraySize} clocks`);
        simCache((StoredResult = (new FSharpResult$2(/* Error */ 1, [makeDummySimulationError("Simulation deleted")])), new SimCache(simCache().Name, simCache().ClockTickRefresh, simCache().StoredState, StoredResult, simulationPlaceholder)));
        const simResult = Result_Bind((tupledArg) => {
            const state = tupledArg[1];
            const design = map((ldc) => {
                if (ldc.Name === diagramName) {
                    return new LoadedComponent(ldc.Name, ldc.TimeStamp, ldc.FilePath, ldc.WaveInfo, state, ldc.InputLabels, ldc.OutputLabels, ldc.LCParameterSlots, ldc.Form, ldc.LoadedComponentIsOutOfDate, ldc.Description, ldc.IsTopSheet, ldc.ArrayInfo);
                }
                else {
                    return ldc;
                }
            }, ldcs);
            if (localBuild) {
                return startCircuitSimulation(simulationArraySize, diagramName, state[0], state[1], design);
            }
            else {
                return designOnlySimulation(simulationArraySize, diagramName, state[0], state[1], design);
            }
        }, Result_MapError(makeDummySimulationError, getStateAndDependencies(diagramName, ldcs)));
        const fastSim = Result_DefaultValue(simulationPlaceholder, Result_Map((sd) => sd.FastSim, simResult));
        simCache(new SimCache(diagramName, simCache().ClockTickRefresh, simCache().StoredState, simResult, fastSim));
        return [simResult, canvasState];
    }
}

